import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset Password — The Hospitality Reserve" },
      { name: "description", content: "Request a password reset link for your Hospitality Reserve member account. We'll email you a secure link to set a new password." },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/reset-password" }],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });
      if (error) throw error;
      setSent(true);
      toast.success("Reset link sent. Check your inbox.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      <header className="px-8 py-6 lg:px-16">
        <Link to="/" className="inline-block">
          <p className="tracking-wide-luxury text-accent">The Hospitality</p>
          <p className="font-display text-lg">Reserve</p>
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="tracking-wide-luxury text-accent">Password Reset</p>
            <span className="gold-divider mt-6" />
            <h1 className="mt-6 font-display text-3xl">Forgot your password?</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Enter your email and we will send you a link to reset it.
            </p>
          </div>

          {sent ? (
            <div className="bg-white text-foreground p-8 text-center">
              <p className="tracking-wide-luxury text-accent mb-4">Check your inbox</p>
              <p className="text-sm text-muted-foreground">
                If an account exists for <strong>{email}</strong>, you will receive a reset link shortly.
              </p>
              <div className="mt-6">
                <Link to="/auth" className="text-xs tracking-wide-luxury text-accent hover:text-accent/80 transition-colors">
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 bg-white text-foreground p-8">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6">
                {loading ? "Please wait…" : "Send Reset Link"}
              </Button>
            </form>
          )}

          {!sent && (
            <div className="mt-6 text-center">
              <Link to="/auth" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent transition-colors">
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
